import { FC } from 'react';

import { HEADER } from '../../consts';
import { useUserDetailsError } from '../../contexts/userDetailsError';
import { useScreenWidth } from '../../hooks/useScreenWidth';
import { pushGTMEvent } from '../../utilities';
import { BurgerMenu } from '../BurgerMenu';
import { Button } from '../Button';
import { HeaderProps } from '../Header';
import { Loader } from '../Loader';
import { MobileUserMenu } from '../MobileUserMenu';
import { MyAccountDetails } from '../MyAccountDetails';
import { HeaderData } from '../services/content';
import { UserActions } from '../UserActions';

import styles from './UserDetails.module.css';

export interface UserDetailsProps {
  auth: HeaderProps['auth'];
  isLoading?: HeaderProps['isLoading'];
  signInLabel: HeaderData['signInLabel'];
  userBalance?: HeaderProps['userBalance'];
  userDetails?: HeaderProps['userDetails'];
}

const renderLoader = (height: number, width1: number, width2: number) => (
  <>
    <Loader height={height} width={width1} />
    <Loader height={height} width={width2} />
  </>
);

export const UserDetails: FC<UserDetailsProps> = ({ auth, isLoading, signInLabel, userBalance, userDetails }) => {
  const isSmallScreenSize = useScreenWidth() < 1024;
  const { userDetailsError } = useUserDetailsError();
  const userDetailsAvailable = !!userDetails && !userDetailsError;
  const { loginProps, logoutProps } = auth;

  const onSignInClick = () => {
    pushGTMEvent(HEADER, signInLabel);
    loginProps.onClick?.();
  };

  const loggedInMobileView = () => (
    <div data-testid="user-details-mobile-loader" className={styles.iconWrapper}>
      {isLoading ? (
        renderLoader(32, 42, 42)
      ) : (
        <>
          <MobileUserMenu loginProps={loginProps} logoutProps={logoutProps} userBalance={userBalance} userDetails={userDetails!} />
          <BurgerMenu isLoggedIn={userDetailsAvailable} loginProps={loginProps} logoutProps={logoutProps} />
        </>
      )}
    </div>
  );

  const loggedOutMobileView = () => (
    <div data-testid="user-details-mobile-loader" className={styles.iconWrapper}>
      {isLoading ? (
        renderLoader(32, 62, 42)
      ) : (
        <>
          <Button disableRouting href={loginProps.href} onPress={onSignInClick} size="sm">
            {signInLabel}
          </Button>
          <BurgerMenu isLoggedIn={userDetailsAvailable} loginProps={loginProps} logoutProps={logoutProps} />
        </>
      )}
    </div>
  );

  const loggedInDesktopView = () =>
    isLoading ? renderLoader(48, 92, 62) : <MyAccountDetails balance={userBalance} logoutProps={logoutProps} name={userDetails!.name} />;

  const loggedOutDesktopView = () =>
    isLoading ? renderLoader(48, 72, 100) : <UserActions logInUrl={loginProps.href} onLoginClick={onSignInClick} />;

  if (userDetailsAvailable) {
    return isSmallScreenSize ? loggedInMobileView() : loggedInDesktopView();
  }

  return isSmallScreenSize ? loggedOutMobileView() : loggedOutDesktopView();
};
